import { renderAssetReference } from './renderers/sys-assets';
import { stableCacheKey, stringifyMap } from './utilities.mjs';

const serializerCache = new Map();

const outboundLinkAttrs = [
  'target="_blank"',
  'rel="external noopener noreferrer"',
];

const BLOCK_LEVEL_MARKUP_PATTERN = /<(figure|div|table|ul|ol)\b/i;

// <p> cannot legally contain block-level elements (figure, div, table, ul, ol) per
// the HTML spec. Browsers auto-close the <p> when parsing such nesting, producing a
// DOM tree that differs from what React rendered server-side -- a hydration mismatch.
function containsBlockLevelMarkup(html) {
  return BLOCK_LEVEL_MARKUP_PATTERN.test(html);
}

// BL-472: Contentstack emits a nested list as a sibling of the <li> it belongs
// under, not inside it. Serialised literally that produces `<ul>…</li><ul>…`,
// where the inner list is a direct child of the outer one — invalid per the
// spec, and what axe reports as a `list` violation. Screen readers announce the
// item count wrong as a result.
//
// Children are already serialised by the time a list handler runs, so the inner
// list is a complete string here and can simply be moved inside the preceding
// item. Depth counting rather than a regex, because nested lists nest again.
function moveNestedListsIntoPreviousItem(child) {
  const NESTED = /<\/li>\s*(?=<(ul|ol)\b)/i;

  let html = child;
  let match = NESTED.exec(html);

  while (match) {
    const listStart = match.index + match[0].length;
    const tag = match[1].toLowerCase();
    const open = new RegExp(`<${tag}\\b`, 'ig');
    const close = new RegExp(`</${tag}\\s*>`, 'ig');

    // Walk forward from the nested list's opening tag until its own close.
    let depth = 0;
    let cursor = listStart;
    let listEnd = -1;

    while (cursor < html.length) {
      open.lastIndex = cursor;
      close.lastIndex = cursor;
      const nextOpen = open.exec(html);
      const nextClose = close.exec(html);

      if (!nextClose) break;

      if (nextOpen && nextOpen.index < nextClose.index) {
        depth += 1;
        cursor = nextOpen.index + nextOpen[0].length;
      } else {
        depth -= 1;
        cursor = nextClose.index + nextClose[0].length;
        if (depth === 0) {
          listEnd = cursor;
          break;
        }
      }
    }

    // Unbalanced markup: leave it alone rather than corrupt it.
    if (listEnd === -1) return html;

    const before = html.slice(0, match.index);
    const nested = html.slice(listStart, listEnd);
    const after = html.slice(listEnd);

    html = `${before}${nested}</li>${after}`;
    match = NESTED.exec(html);
  }

  return html;
}

function filterAttributes(attrs, allowedAttrs = []) {
  if (!attrs || allowedAttrs.length === 0) return '';

  const attrsMap = new Map();

  // Match all attributes using regex (handles spaces in values)
  const attrMatches = attrs.matchAll(/(\w+(?:-\w+)*)="([^"]*)"/g);

  for (const match of attrMatches) {
    const [, key, value] = match;
    // Only include if it's in the allowed list
    if (allowedAttrs.includes(key)) {
      attrsMap.set(key, value);
    }
  }

  return attrsMap.size > 0 ? ' ' + stringifyMap(attrsMap) : '';
}

export function customHTMLSerializer(options = {}) {
  const cacheKey = stableCacheKey(options);

  if (serializerCache.has(cacheKey)) {
    return serializerCache.get(cacheKey);
  }

  const { allowedAttrs = {}, stripBlockElements = false, strongClassName = 'font-bold' } = options;

  const serializer = {
    customElementTypes: {
      'social-embed': (attrs, child) => {
        return `<social-embed${attrs}>${child}</social-embed>`;
      },
      h1: (attrs, child) => {
        if (stripBlockElements) return child;
        const h1AllowedAttrs = allowedAttrs.h1 || allowedAttrs.all || [];
        const filteredAttrs = filterAttributes(attrs, h1AllowedAttrs);

        return `<h1${filteredAttrs}>${child}</h1>`;
      },
      h2: (attrs, child) => {
        if (stripBlockElements) return child;
        const h2AllowedAttrs = allowedAttrs.h2 || allowedAttrs.all || [];
        const filteredAttrs = filterAttributes(attrs, h2AllowedAttrs);

        return `<h2 class="text-4xl mb-4 font-semibold"${filteredAttrs}>${child}</h2>`;
      },
      h3: (attrs, child) => {
        if (stripBlockElements) return child;
        const h3AllowedAttrs = allowedAttrs.h3 || allowedAttrs.all || [];
        const filteredAttrs = filterAttributes(attrs, h3AllowedAttrs);

        return `<h3 class="text-lg md:text-3xl mb-4 font-semibold"${filteredAttrs}>${child}</h3>`;
      },
      h4: (attrs, child) => {
        if (stripBlockElements) return child;
        const h4AllowedAttrs = allowedAttrs.h4 || allowedAttrs.all || [];
        const filteredAttrs = filterAttributes(attrs, h4AllowedAttrs);

        return `<h4 class="text-lg md:text-xl font-semibold mb-4"${filteredAttrs}>${child}</h4>`;
      },
      h5: (attrs, child) => {
        if (stripBlockElements) return child;
        const h5AllowedAttrs = allowedAttrs.h5 || allowedAttrs.all || [];
        const filteredAttrs = filterAttributes(attrs, h5AllowedAttrs);

        return `<h5 class="text-lg md:text-xl font-semibold mb-4"${filteredAttrs}>${child}</h5>`;
      },
      h6: (attrs, child) => {
        if (stripBlockElements) return child;
        const h6AllowedAttrs = allowedAttrs.h6 || allowedAttrs.all || [];
        const filteredAttrs = filterAttributes(attrs, h6AllowedAttrs);

        return `<h6 class="text-lg md:text-xl font-semibold mb-4"${filteredAttrs}>${child}</h6>`;
      },
      p: (attrs, child) => {
        if (stripBlockElements) return child;
        if (containsBlockLevelMarkup(child)) return child;
        const pAllowedAttrs = allowedAttrs.p || allowedAttrs.all || [];
        const filteredAttrs = filterAttributes(attrs, pAllowedAttrs);

        return `<p class="mb-4"${filteredAttrs}>${child}</p>`;
      },
      a: (attrs, child) => {
        // attrs contains target="_blank" if you check "Open link in new tab
        const isOutboundLink = attrs.includes('target="_blank"');
        const attrsArr = attrs.split(' ');
        const attrsMap = new Map();

        // Parse attributes into a key-value map
        attrsArr.forEach((attr) => {
          const match = attr.match(/([^=]+)="([^"]+)"/);

          if (match && !match[1].startsWith('url')) {
            const [, key, value] = match;
            attrsMap.set(key, value);
          }
        });

        if (!isOutboundLink) {
          return `<a ${stringifyMap(attrsMap)} class="underline cursor-pointer text-sea">${child}</a>`;
        }

        // BL-701: written on one line on purpose. The indentation this used to
        // carry became real text nodes inside the anchor — a 5px clickable gap
        // before the first letter, and another 5px between the label and the
        // icon. The leading one made outbound links sit visibly offset from the
        // text before them. The gap next to the icon is wanted, so it comes
        // back as `ml-1` where it can be seen and changed, rather than as a
        // side effect of source formatting. In place since #71 (Apr 30), so
        // this affects every outbound RTE link on the site, not just this
        // block.
        return `<a ${stringifyMap(attrsMap)} class="text-sea cursor-pointer relative" ${outboundLinkAttrs.join(' ')}><span class="underline">${child}</span><svg class="inline ml-1" aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.0002 5H8.2002C7.08009 5 6.51962 5 6.0918 5.21799C5.71547 5.40973 5.40973 5.71547 5.21799 6.0918C5 6.51962 5 7.08009 5 8.2002V15.8002C5 16.9203 5 17.4801 5.21799 17.9079C5.40973 18.2842 5.71547 18.5905 6.0918 18.7822C6.5192 19 7.07899 19 8.19691 19H15.8031C16.921 19 17.48 19 17.9074 18.7822C18.2837 18.5905 18.5905 18.2839 18.7822 17.9076C19 17.4802 19 16.921 19 15.8031V14M20 9V4M20 4H15M20 4L13 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="sr-only"> (opens in a new tab)</span></a>`;
      },
      img: (attrs) => {
        const attrsArr = attrs.match(/([a-zA-Z0-9_-]+)="([^"]*)"/g) || [];
        const attrsMap = new Map();
        let caption = '';

        attrsArr.forEach((attr) => {
          const match = attr.match(/([a-zA-Z0-9_-]+)="([^"]*)"/);
          if (match) {
            const [, key, value] = match;
            if (key === 'caption') caption = value;
            if (key === 'url' || key === 'position' || key === 'caption')
              return;
            attrsMap.set(key, value);
          }
        });

        const imgAttrsMap = new Map(attrsMap);
        imgAttrsMap.delete('anchorLink');
        imgAttrsMap.delete('target');

        const mainTag = `<figure class="mb-6">
        <img ${stringifyMap(imgAttrsMap)} class="w-full h-auto" loading="lazy" decoding="async" />
        ${/* gray-600, not 500: on warm-white panels the lighter grey was 4.35:1 (BL-471). */ ''}
        ${caption ? `<figcaption class="text-sm text-gray-600 mt-2">${caption}</figcaption>` : ''}
      </figure>`;

        // If you add a link to the image in contentstack, it gets the anchorLink attribute
        if (attrsMap.has('anchorLink')) {
          const anchorLink = attrsMap.get('anchorLink');
          const target = attrsMap.get('target') || '';

          const relAttr =
            target === '_blank' ? ' rel="noopener noreferrer"' : '';
          const srOnly =
            target === '_blank'
              ? '<span class="sr-only"> (opens in a new tab)</span>'
              : '';

          return `<a href="${anchorLink}" ${target ? `target="${target}"` : ''}${relAttr} class="block">${mainTag}${srOnly}</a>`;
        }

        return mainTag;
      },
      ul: (attrs, child) => {
        if (stripBlockElements) return child;
        const ulAllowedAttrs = allowedAttrs.ul || allowedAttrs.all || [];
        const filteredAttrs = filterAttributes(attrs, ulAllowedAttrs);

        return `<ul class="list-disc pl-4 mb-4 text-left"${filteredAttrs}>${moveNestedListsIntoPreviousItem(child)}</ul>`;
      },
      ol: (attrs, child) => {
        if (stripBlockElements) return child;
        const olAllowedAttrs = allowedAttrs.ol || allowedAttrs.all || [];
        const filteredAttrs = filterAttributes(attrs, olAllowedAttrs);

        return `<ol class="list-decimal pl-4 mb-4 text-left"${filteredAttrs}>${moveNestedListsIntoPreviousItem(child)}</ol>`;
      },
      li: (attrs, child) => {
        if (stripBlockElements) return child;
        const liAllowedAttrs = allowedAttrs.li || allowedAttrs.all || [];
        const filteredAttrs = filterAttributes(attrs, liAllowedAttrs);

        return `<li${filteredAttrs}>${child}</li>`;
      },
      div: (attrs, child) => {
        if (stripBlockElements) return child;
        const divAllowedAttrs = allowedAttrs.div || allowedAttrs.all || [];
        const filteredAttrs = filterAttributes(attrs, divAllowedAttrs);

        return `<div${filteredAttrs}>${child}</div>`;
      },
      u: (_attrs, child) => `<span class="underline">${child}</span>`,
      table: (_attrs, child) =>
        stripBlockElements
          ? child
          : `<table class="w-full border-collapse border border-gray-300 my-4">${child}</table>`,
      thead: (_attrs, child) =>
        stripBlockElements
          ? child
          : `<thead class="bg-gray-100">${child}</thead>`,
      tbody: (_attrs, child) =>
        stripBlockElements ? child : `<tbody>${child}</tbody>`,
      tr: (_attrs, child) =>
        stripBlockElements
          ? child
          : `<tr class="border-b border-gray-300">${child}</tr>`,
      th: (_attrs, child) =>
        stripBlockElements
          ? child
          : `<th class="px-4 py-2 text-left font-semibold border-r border-gray-300">${child}</th>`,
      td: (_attrs, child) =>
        stripBlockElements
          ? child
          : `<td class="px-4 py-2 border-r border-gray-300">${child}</td>`,
      blockquote: (_attrs, child) =>
        stripBlockElements ? child : `<blockquote>${child}</blockquote>`,
      reference: (_attrs, _child, jsonBlock) => {
        const contentType = jsonBlock?.attrs?.['content-type-uid'];
        if (!contentType) return '';

        switch (contentType) {
          case 'sys_assets':
            return renderAssetReference(_attrs, _child, jsonBlock);
          default:
            return '';
        }
      },
      embed: (_attrs, child) => {
        return `
        <div class="relative w-full" style="padding-bottom: 56.25%;">
          <iframe
            ${_attrs}
            class="absolute top-0 left-0 w-full h-full"
            frameborder="0"
            allowfullscreen
          >
            ${child}
          </iframe>
        </div>
      `;
      },
    },
    // BL-785: Contentstack encodes bold as a `bold: true` mark on the text
    // node, not a `strong` element — `customElementTypes.strong` never fires
    // for it. `customTextWrapper` is the map that actually intercepts marks.
    customTextWrapper: {
      bold: (child) => (strongClassName ? `<span class="${strongClassName}">${child}</span>` : child),
    },
  };

  serializerCache.set(cacheKey, serializer);

  return serializer;
}
